import { injectWithTransform, singleton } from "tsyringe";
import { Logger, LoggerTransformer } from "@identity/utils";
import { Providers } from "./providers";

/**
 * This is simple and lightweight implementation of TCF API.
 * It is only being used in US.
 * In GDPR countries we use full TCF API from @iabtcf/cmpapi package.
 * @see: ./tcf-api.ts
 */

@singleton()
export class SimpleTcfApi {
  private static readonly FANDOM_CMP_ID = 141;
  private readonly cmpIdMap: Record<Providers, number> = {
    [Providers.AIRGAP]: 399,
    [Providers.COOKIEBOT]: 134,
    [Providers.ONETRUST]: 28,
    [Providers.KETCH]: 340,
  };

  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "TCF")
    private logger: Logger,
  ) {}

  addLocatorFrame() {
    const name = "____tcfapiLocator";

    if (window.frames[name]) {
      return;
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.addLocatorFrame(),
      );
      return;
    }

    const body = document.body,
      iframe = document.createElement("iframe");
    iframe.name = name;
    iframe.style.display = "none";
    body.appendChild(iframe);
  }

  install(provider?: Providers) {
    const cmpId =
      provider !== undefined
        ? (this.cmpIdMap[provider] ?? SimpleTcfApi.FANDOM_CMP_ID)
        : SimpleTcfApi.FANDOM_CMP_ID;
    function cmpMsgHandler(event: any) {
      try {
        let json = event.data;
        const msgIsString = typeof json === "string";
        if (msgIsString) {
          try {
            json = JSON.parse(json);
          } catch (error) {} // eslint-disable-line no-empty
        }
        const call = json.__tcfapiCall;
        if (call) {
          window.__tcfapi(
            call.command,
            call.parameter,
            function (retValue, success) {
              const returnMsg = {
                __tcfapiReturn: {
                  returnValue: retValue,
                  success: success,
                  callId: call.callId,
                },
              };
              event.source.postMessage(
                msgIsString ? JSON.stringify(returnMsg) : returnMsg,
                "*",
              );
            },
          );
        }
      } catch (e) {} // eslint-disable-line no-empty
    }

    function cmpFunc(
      command: string,
      version: number,
      callback: (data: any, success: boolean) => void,
    ) {
      if (
        command === "addEventListener" ||
        command === "getTCData" ||
        command === "ping"
      ) {
        callback(
          {
            cmpId,
            eventStatus: "tcloaded",
            cmpLoaded: true,
            cmpStatus: "loaded",
            cmpVersion: version,
            displayStatus: "disabled",
            apiVersion: "2.2",
            tcfPolicyVersion: 5,
            gdprApplies: false,
          },
          true,
        );
      } else {
        callback(undefined, false);
      }
    }

    if (window.__tcfapi) {
      window.__tcfapi("ping", 2, (pingReturn) => {
        if (pingReturn?.cmpStatus === "stub") {
          window.__tcfapi = cmpFunc;
          // @ts-expect-error __tcfapi stub
          window.__tcfapi.msgHandler = cmpMsgHandler;
          this.addLocatorFrame();
          window.addEventListener("message", cmpMsgHandler, false);
          this.logger.info("__tcfapi installed");
        }
      });
    } else {
      window.__tcfapi = cmpFunc;
      // @ts-expect-error tcfapi stub
      window.__tcfapi.msgHandler = cmpMsgHandler;
      this.addLocatorFrame();
      window.addEventListener("message", cmpMsgHandler, false);
      this.logger.info("__tcfapi installed");
    }
  }
}
